import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { api, BootstrapResult } from './api';
import { Review, UserDoc } from './domain/types';

interface AppDataState extends BootstrapResult {
  users: UserDoc[];
  reviews: Review[];
  reviewByTask: Record<string, Review>;
  reload: () => Promise<void>;
}

const Ctx = createContext<AppDataState | null>(null);
export const useAppData = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAppData fuera de AppDataProvider');
  return v;
};

export function AppDataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<BootstrapResult | null>(null);
  const [users, setUsers] = useState<UserDoc[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [err, setErr] = useState('');

  const reload = useCallback(async () => {
    const [boot, us] = await Promise.all([api.bootstrap(), api.listAssignableUsers()]);
    setData(boot);
    setUsers(us);
    // Las revisiones son legibles (RLS) por admin/executive y por el
    // responsable de espacio (scopeadas a sus espacios).
    // Resiliente: si la migración aún no está aplicada, no romper la app.
    if (boot.role === 'admin' || boot.role === 'executive' || boot.role === 'space_manager') {
      try { setReviews(await api.listReviews()); }
      catch { setReviews([]); }
    } else {
      setReviews([]);
    }
  }, []);

  useEffect(() => {
    reload().catch((e) => setErr(e?.message || 'Error cargando datos.'));
  }, [reload]);

  if (err) return <div className="container"><div className="error">{err}</div></div>;
  if (!data) return <div className="center"><div className="muted">Cargando…</div></div>;

  const reviewByTask: Record<string, Review> = {};
  for (const r of reviews) reviewByTask[r.task_id] = r;

  return <Ctx.Provider value={{ ...data, users, reviews, reviewByTask, reload }}>{children}</Ctx.Provider>;
}
